import React, { useRef, useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { VideoPlayer, Portal } from 'components/common';
import classes from './VideoAnimation.module.scss';
import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import { useOnDemandVideo } from 'mobile-modules/offline/video';
import { logOfflineDiagnosticEvent } from 'mobile-modules/offline/assets';
import { resolveMobileLoadingLocale } from 'components/common/mobileLoadingMessages';

// Native video loaders previously used hard-coded English before the first
// Custom Level activation. Use the actual UI/Help locale, not Activity locale.
const MOBILE_VIDEO_TEXT = {
  en: {
    introduction: 'Preparing introduction video...',
    preparing: 'Preparing video...',
    unavailable: 'Introduction video unavailable.',
    offline: 'This video has not been downloaded. Connect to the internet to watch it.',
    failed: 'Unable to load this video.',
    play: 'Play video',
  },
  es: {
    introduction: 'Preparando el video de introducción...',
    preparing: 'Preparando el video...',
    unavailable: 'El video de introducción no está disponible.',
    offline: 'Este video no se ha descargado. Conéctate a internet para verlo.',
    failed: 'No se pudo cargar el video.',
    play: 'Reproducir video',
  },
  hc: {
    introduction: 'Ap prepare videyo entwodiksyon an...',
    preparing: 'Ap prepare videyo a...',
    unavailable: 'Videyo entwodiksyon an pa disponib.',
    offline: 'Videyo sa a poko telechaje. Konekte sou entènèt pou gade li.',
    failed: 'Nou pa ka chaje videyo sa a.',
    play: 'Jwe videyo a',
  },
  vm: {
    introduction: 'Đang chuẩn bị video giới thiệu...',
    preparing: 'Đang chuẩn bị video...',
    unavailable: 'Không có video giới thiệu.',
    offline: 'Video này chưa được tải xuống. Hãy kết nối Internet để xem.',
    failed: 'Không thể tải video này.',
    play: 'Phát video',
  },
};

const nativePresentationCode = (loginCode, solutionCode) =>
  String(loginCode || solutionCode || 'en').toLowerCase();

const VideoAnimation = ({ video, loginTranslate, playTranslate, solutionLocaleCode, loginLocaleCode, generatedCustomLevelActive, nativeSessionLocaleCode }) => {
  const translate = playTranslate || loginTranslate;
  // During a newly generated Level, Play locale can still be the Placement
  // Activity language on its first render. Login UI locale is authoritative.
  const mobilePlatform = getMobileAppPlatform();
  const nativeMobile = mobilePlatform === 'android' || mobilePlatform === 'ios';
  const previousPresentationCode = nativePresentationCode(
    generatedCustomLevelActive ? loginLocaleCode : '', solutionLocaleCode
  );
  const presentationCode = nativeMobile
    ? (nativeSessionLocaleCode || previousPresentationCode)
    : previousPresentationCode;
  const videoText = MOBILE_VIDEO_TEXT[presentationCode] || MOBILE_VIDEO_TEXT.en;
  const [loaded, setLoaded] = useState(false);
  const [manualPlayNeeded, setManualPlayNeeded] = useState(false);
  const [quickStartPlaying, setQuickStartPlaying] = useState(false);
  const videoRef = useRef();
  const nativeVideoRef = useRef();
  const startedRef = useRef(false);

  const { src, callback, stopOnTap = false, onReady } = video;
  const mobileQuickStart = !stopOnTap && nativeMobile;
  const customLevelIntro = stopOnTap && nativeMobile;
  const requestedVideo = useOnDemandVideo(
    (mobileQuickStart || customLevelIntro) ? src : '',
    customLevelIntro ? 'custom-level-intro' : 'quickstart-video'
  );
  const completedRef = useRef(false);
  const readyNotifiedRef = useRef(false);
  const lastLocaleDiagnostic = useRef('');

  useEffect(() => {
    if (!video || !nativeMobile || (!mobileQuickStart && !customLevelIntro)) return;
    const renderedText = requestedVideo.status === 'loading'
      ? (customLevelIntro ? videoText.introduction : videoText.preparing)
      : requestedVideo.status === 'ready'
        ? ''
        : customLevelIntro ? videoText.unavailable
          : requestedVideo.status === 'offline-missing' ? videoText.offline : videoText.failed;
    const signature = JSON.stringify([requestedVideo.status, presentationCode, renderedText]);
    if (lastLocaleDiagnostic.current === signature) return;
    lastLocaleDiagnostic.current = signature;
    const record = {
      screen: 'VideoAnimation',
      videoKind: customLevelIntro ? 'introduction' : 'quickstart',
      videoStatus: requestedVideo.status,
      renderedText,
      appliedLocale: presentationCode,
      solutionLocaleCode,
      statusLocaleCode: loginLocaleCode,
      localeSource: generatedCustomLevelActive ? 'status.lang-first' : 'flinkPlay.solutionLocale-first',
    };
    console.info('[FLC][loader-locale]', record);
    logOfflineDiagnosticEvent('mobile-loader-locale', record);
  }, [video, nativeMobile, mobileQuickStart, customLevelIntro, requestedVideo.status,
    presentationCode, videoText, solutionLocaleCode, loginLocaleCode, generatedCustomLevelActive]);

  const finishVideo = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;

    const activeRef = stopOnTap ? nativeVideoRef : videoRef;
    callback(activeRef);
  }, [callback, stopOnTap]);

  const callCallbackIfVideoStarted = useCallback(() => {
    if (
      videoRef.current &&
      videoRef.current.player &&
      videoRef.current.player.isPlaying
    ) {
      finishVideo();
    }
  }, [finishVideo]);

  const stopDesktopVideo = useCallback((event) => {
    if (stopOnTap) return;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // QuickStart uses an empty callback argument as the explicit close signal.
    // Keep the ref-based path for natural end/error so existing preview behavior
    // is unchanged, but a desktop user click must actually remove the overlay.
    completedRef.current = true;
    callback();
  }, [callback, stopOnTap]);

  useEffect(() => {
    if (!video || stopOnTap || mobileQuickStart) return undefined;

    document.addEventListener('keydown', callCallbackIfVideoStarted);
    document.addEventListener('click', callCallbackIfVideoStarted);

    return () => {
      document.removeEventListener('keydown', callCallbackIfVideoStarted);
      document.removeEventListener('click', callCallbackIfVideoStarted);
    };
  }, [video, stopOnTap, mobileQuickStart, callCallbackIfVideoStarted]);

  const stopMobileIntro = useCallback((event) => {
    if (!stopOnTap) return;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    try {
      nativeVideoRef.current?.pause?.();
    } catch (error) {
      // The completion callback below is the authoritative stop path.
    }

    finishVideo();
  }, [finishVideo, stopOnTap]);

  const closeMobileQuickStart = useCallback((event) => {
    if (!mobileQuickStart || completedRef.current) return;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    completedRef.current = true;
    try {
      nativeVideoRef.current?.pause?.();
    } catch (error) {
      // Closing the Redux video overlay is authoritative.
    }

    // QuickStart treats an empty callback argument as an explicit close signal.
    callback();
  }, [mobileQuickStart, callback]);

  const notifyNativeReady = useCallback(() => {
    setLoaded(true);
    if (readyNotifiedRef.current || typeof onReady !== 'function') return;
    readyNotifiedRef.current = true;
    Promise.resolve(onReady()).catch((error) => {
      console.error('[mobile-video][ready-callback-error]', error);
    });
  }, [onReady]);

  const handleNativePlay = useCallback(() => {
    setManualPlayNeeded(false);
    startedRef.current = true;
    setLoaded(true);
  }, []);

  const ensureQuickStartPlayback = useCallback(() => {
    try {
      // A file is now loaded asynchronously after the original click. iOS may
      // no longer grant audible autoplay; expose a user-gesture Play button.
      const attempt = nativeVideoRef.current?.play?.();
      Promise.resolve(attempt).catch(() => setManualPlayNeeded(true));
    } catch (_) {
      setManualPlayNeeded(true);
    }
  }, []);

  const playQuickStartManually = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    ensureQuickStartPlayback();
  }, [ensureQuickStartPlayback]);

  useEffect(() => {
    if (!customLevelIntro) return;
    if (requestedVideo.status === 'error' || requestedVideo.status === 'offline-missing') {
      console.error('[mobile-custom-content][intro-video-unavailable]', requestedVideo.status, src);
      finishVideo();
    }
  }, [customLevelIntro, requestedVideo.status, src, finishVideo]);

  const handleNativePause = useCallback(() => {
    // iOS may still switch to its native player on some WebView configurations.
    // If the user taps Pause there, treat that first interaction as "Stop Intro"
    // so they never have to close the native player and tap the 34% loader again.
    if (stopOnTap && startedRef.current && !completedRef.current) {
      finishVideo();
    }
  }, [finishVideo, stopOnTap]);

  if (!video) return null;

  if (stopOnTap) {
    // The mobile startup loader is rendered through Portal with z-index 2200.
    // Render the intro video through the same top-level portal so it cannot be
    // trapped underneath FlinkPlay .content's stacking context.
    return (
      <Portal>
        <div
          className={`${classes.videoOverlay} ${classes.mobileIntroOverlay} ${classes.loaded}`}
        >
          {requestedVideo.status === 'ready' ? (
            <video
              ref={nativeVideoRef}
              src={requestedVideo.url}
              autoPlay
              playsInline
              controls={false}
              disablePictureInPicture
              preload="auto"
              onLoadedData={notifyNativeReady}
              onCanPlay={() => { notifyNativeReady(); ensureQuickStartPlayback(); }}
              onPlay={handleNativePlay}
              onPause={handleNativePause}
              onError={finishVideo}
              onEnded={finishVideo}
              className={classes.mobileIntroVideo}
            />
          ) : (
            <p className={classes.mobileIntroPreparing}>
              {requestedVideo.status === 'loading' ? videoText.introduction : videoText.unavailable}
            </p>
          )}
          {manualPlayNeeded && requestedVideo.status === 'ready' && (
            <button
              type="button"
              className={classes.mobileQuickStartManualPlay}
              onClick={playQuickStartManually}
            >
              {videoText.play}
            </button>
          )}

          <button
            type="button"
            aria-label={translate(78, 'Click Here to Stop')}
            onPointerDown={stopMobileIntro}
            onTouchStart={stopMobileIntro}
            onClick={stopMobileIntro}
            className={classes.mobileIntroStopLayer}
          >
            <span
              className={classes.overlayText}
              style={{ pointerEvents: 'none' }}
            >
              {translate(78, 'Click Here to Stop')}
            </span>
          </button>
        </div>
      </Portal>
    );
  }

  if (mobileQuickStart) {
    const preparingVideo = requestedVideo.status === 'loading'
      || (requestedVideo.status === 'ready' && !quickStartPlaying && !manualPlayNeeded);
    return (
      <Portal>
        <div
          // Keep Close and download status visible while the requested file is
          // being cached. The original overlay stays hidden until onLoadedData.
          className={`${classes.videoOverlay} ${classes.mobileIntroOverlay} ${classes.loaded}`}
        >
          {requestedVideo.status === 'ready' ? (
            <video
              ref={nativeVideoRef}
              src={requestedVideo.url}
              autoPlay
              playsInline
              controls={false}
              disablePictureInPicture
              preload="auto"
              onLoadedData={notifyNativeReady}
              onCanPlay={() => { notifyNativeReady(); ensureQuickStartPlayback(); }}
              onPlay={handleNativePlay}
              onPlaying={() => setQuickStartPlaying(true)}
              onWaiting={() => setQuickStartPlaying(false)}
              onError={closeMobileQuickStart}
              onEnded={closeMobileQuickStart}
              className={classes.mobileIntroVideo}
            />
          ) : null}

          {preparingVideo && (
            <div className={classes.mobileVideoPreparing} role="status" aria-live="polite">
              <span className={classes.mobileVideoSpinner} aria-hidden="true" />
              <span>{videoText.preparing}</span>
            </div>
          )}
          {requestedVideo.status !== 'loading' && requestedVideo.status !== 'ready' && (
            <div className={classes.mobileVideoPreparing} role="alert">
              {requestedVideo.status === 'offline-missing' ? videoText.offline : videoText.failed}
            </div>
          )}

          {manualPlayNeeded && requestedVideo.status === 'ready' && (
            <button
              type="button"
              className={classes.mobileQuickStartManualPlay}
              onClick={playQuickStartManually}
            >
              {videoText.play}
            </button>
          )}

          <button
            type="button"
            aria-label={translate(78, 'Click Here to Stop')}
            onPointerDown={closeMobileQuickStart}
            onTouchStart={closeMobileQuickStart}
            onClick={closeMobileQuickStart}
            className={classes.mobileIntroStopLayer}
          >
            {quickStartPlaying && (
              <span
                className={classes.overlayText}
                style={{ pointerEvents: 'none' }}
              >
                {translate(78, 'Click Here to Stop')}
              </span>
            )}
          </button>
        </div>
      </Portal>
    );
  }

  return (
    <div
      className={classes.videoOverlay + (loaded ? ` ${classes.loaded}` : '')}
      onClick={stopDesktopVideo}
    >
      <p className={classes.overlayText}>
        {translate(78, 'Click Here to Stop')}
      </p>

      <VideoPlayer
        ref={videoRef}
        playing
        width="100%"
        height="100%"
        url={src}
        onReady={() => setLoaded(true)}
        onError={finishVideo}
        onEnded={finishVideo}
      />
    </div>
  );
};

VideoAnimation.propTypes = {
  video: PropTypes.object,
  playTranslate: PropTypes.func,
  loginTranslate: PropTypes.func,
  solutionLocaleCode: PropTypes.string,
  loginLocaleCode: PropTypes.string,
  generatedCustomLevelActive: PropTypes.bool,
  nativeSessionLocaleCode: PropTypes.string,
};

const mapStateToProps = ({ flinkPlay, status }) => ({
  playTranslate: flinkPlay.solutionTranslate,
  loginTranslate: status.translate,
  solutionLocaleCode: flinkPlay.solutionLocale?.code || '',
  loginLocaleCode: status.lang?.code || '',
  generatedCustomLevelActive: flinkPlay.solutionMenu?.generatedCustomLevelIndex != null,
  nativeSessionLocaleCode: resolveMobileLoadingLocale({ flinkPlay, status }).code,
  video: flinkPlay.video,
});

export default connect(mapStateToProps)(VideoAnimation);
